import { CircleAlert, LockKeyhole, SearchX } from "lucide-react";
import type { JSX, ReactNode } from "react";

import { Button } from "./button";

/**
 * Empty state: one line and one button. No illustration, no paragraph of guidance.
 * `action` is optional; a list that is empty because of a filter needs a reset, not a
 * create button.
 */
export function EmptyState({
  message,
  action,
}: {
  message: string;
  action?: { label: string; onClick: () => void } | undefined;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <SearchX className="size-5 text-ink-subtle" strokeWidth={1.5} aria-hidden="true" />
      <p className="text-base text-ink-muted">{message}</p>
      {action === undefined ? null : (
        <Button variant="secondary" size="sm" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
}

/** A failed request. Says what failed and offers a retry; never a stack trace. */
export function ErrorState({
  message,
  retryLabel,
  onRetry,
}: {
  message: string;
  retryLabel: string;
  onRetry?: (() => void) | undefined;
}): JSX.Element {
  return (
    <div role="alert" className="flex flex-col items-center gap-3 py-8 text-center">
      <CircleAlert className="size-5 text-danger-ink" strokeWidth={1.5} aria-hidden="true" />
      <p className="text-base text-ink">{message}</p>
      {onRetry === undefined ? null : (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

/** The role may not see this. Stated plainly; the screen is not hidden behind it. */
export function NoPermission({ message }: { message: string }): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <LockKeyhole className="size-5 text-ink-subtle" strokeWidth={1.5} aria-hidden="true" />
      <p className="text-base text-ink-muted">{message}</p>
    </div>
  );
}

/** A labelled group of state specimens, for the ui-kit page. */
export function StateSpecimen({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-ink-subtle">{label}</span>
      <div className="rounded-card border border-line bg-surface">{children}</div>
    </div>
  );
}
